// ===== K-Tec Product Recommendation Engine =====
// Recommends products from catalog based on parsed requirements

import type { ParsedRequirement } from './requirementParser';
import type { CatalogProduct, CCTVPackage } from '../admin/types/catalog';

export interface ProductRecommendation {
  product: CatalogProduct;
  reason: string;
  quantity: number;
  role: 'camera' | 'nvr' | 'dvr' | 'hdd' | 'poe-switch' | 'cable' | 'accessory';
}

export interface SolutionRecommendation {
  title: string;
  cameras: ProductRecommendation[];
  recorder?: ProductRecommendation;
  storage?: ProductRecommendation;
  network?: ProductRecommendation;
  accessories: ProductRecommendation[];
  installationCost: number;
  labourCost: number;
  totalEstimate: number;
  notes: string[];
  storageEstimate?: string;
}

function getProductScore(product: CatalogProduct, req: ParsedRequirement): number {
  let score = 0;
  
  // Brand preference
  if (req.preferredBrand && product.brandId) {
    const brandNames: Record<string, string> = { b1: 'Dahua', b2: 'Hikvision', b3: 'EZVIZ', b4: 'IMOU' };
    if (brandNames[product.brandId] === req.preferredBrand) score += 10;
  }

  // Full colour support
  if (req.fullColour) {
    const desc = (product.shortDescription + (product.name || '')).toLowerCase();
    if (desc.includes('full colour') || desc.includes('full color') || desc.includes('colorvu') || 
        desc.includes('colour') || desc.includes('smart dual light') || desc.includes('full-color')) {
      score += 15;
    }
  }

  // Stock availability
  if (product.stock > 0 && product.inStock) score += 5;
  if (product.stock > 10) score += 2;

  // Featured / bestseller
  if (product.isFeatured) score += 3;
  if (product.isBestSeller) score += 3;

  // Price within budget per unit
  if (req.budget && req.cameraQuantity) {
    const maxPerUnit = req.budget / (req.cameraQuantity * 2); // rough estimate
    if (product.salePrice && product.salePrice <= maxPerUnit) score += 5;
  }

  return score;
}

export function recommendCameras(
  products: CatalogProduct[],
  req: ParsedRequirement
): ProductRecommendation[] {
  // Filter for camera products
  let cameras = products.filter(p => {
    if (!p.inStock || p.stock <= 0) return false;
    if (p.status !== 'active') return false;
    const name = p.name.toLowerCase();
    const cat = p.subcategoryId || '';
    // Camera subcategories
    return ['sub2', 'sub3', 'sub6', 'sub7', 'sub8', 'sub9', 'sub10', 'sub11', 'sub12', 'sub1'].includes(cat) ||
      name.includes('camera') || name.includes('ipc') || name.includes('hac-') || 
      name.includes('ipc-') || name.includes('hdw') || name.includes('hfw');
  });

  // Wi-Fi filter
  if (req.wifiCamera) {
    const wifiCams = cameras.filter(p => 
      ['sub3'].includes(p.subcategoryId || '') ||
      p.name.toLowerCase().includes('wifi') || 
      p.name.toLowerCase().includes('wi-fi') ||
      p.shortDescription.toLowerCase().includes('wifi')
    );
    if (wifiCams.length > 0) cameras = wifiCams;
  }

  // Sort by score
  cameras.sort((a, b) => getProductScore(b, req) - getProductScore(a, req));

  if (cameras.length === 0) return [];

  const topCamera = cameras[0];
  const qty = req.cameraQuantity || 4;

  return [{
    product: topCamera,
    reason: req.fullColour 
      ? 'Full colour night vision camera - records in colour even at night'
      : req.wifiCamera 
      ? 'Wi-Fi camera - no cable required, easy to install'
      : 'High quality IP camera with excellent image quality',
    quantity: qty,
    role: 'camera',
  }];
}

export function recommendRecorder(
  products: CatalogProduct[],
  cameraCount: number,
  isWifi: boolean
): ProductRecommendation | undefined {
  if (isWifi && cameraCount <= 4) {
    // WiFi cameras may not need NVR
    return undefined;
  }

  // Find NVR/DVR
  const recorders = products.filter(p => {
    if (!p.inStock || p.stock <= 0) return false;
    if (p.status !== 'active') return false;
    return ['sub13', 'sub14'].includes(p.subcategoryId || '') ||
      p.name.toLowerCase().includes('nvr') ||
      p.name.toLowerCase().includes('dvr') ||
      p.name.toLowerCase().includes('xvr');
  });

  if (recorders.length === 0) return undefined;

  // Find one with enough channels
  const suitable = recorders.filter(p => {
    const name = p.name.toLowerCase();
    // Check channel count in name
    if (cameraCount <= 4) return name.includes('4ch') || name.includes('4 ch') || name.includes('4channel') || name.includes('4-ch');
    if (cameraCount <= 8) return name.includes('8ch') || name.includes('8 ch') || name.includes('8channel') || name.includes('8-ch');
    if (cameraCount <= 16) return name.includes('16ch') || name.includes('16 ch') || name.includes('16channel') || name.includes('16-ch');
    return true;
  });

  const recorder = suitable.length > 0 ? suitable[0] : recorders[0];
  
  const channelText = cameraCount <= 4 ? '4-Channel' : cameraCount <= 8 ? '8-Channel' : '16-Channel';
  
  return {
    product: recorder,
    reason: `${channelText} recorder - handles all ${cameraCount} cameras`,
    quantity: 1,
    role: 'nvr',
  };
}

export function recommendStorage(
  products: CatalogProduct[],
  cameraCount: number,
  storageDays: number = 30
): ProductRecommendation | undefined {
  const hdds = products.filter(p => {
    if (!p.inStock || p.stock <= 0) return false;
    if (p.status !== 'active') return false;
    return ['sub15'].includes(p.subcategoryId || '') ||
      p.name.toLowerCase().includes('hdd') ||
      p.name.toLowerCase().includes('hard drive') ||
      p.name.toLowerCase().includes('surveillance') && p.name.toLowerCase().includes('tb');
  });

  if (hdds.length === 0) return undefined;

  // Estimate storage: ~8GB per camera per day for 1080p
  const estimatedGB = cameraCount * 8 * storageDays;
  const estimatedTB = Math.ceil(estimatedGB / 1000);

  // Find suitable HDD
  const suitable = hdds.filter(p => {
    const name = p.name.toLowerCase();
    if (estimatedTB <= 1) return name.includes('1tb') || name.includes('1 tb');
    if (estimatedTB <= 2) return name.includes('2tb') || name.includes('2 tb');
    if (estimatedTB <= 4) return name.includes('4tb') || name.includes('4 tb');
    return name.includes('4tb') || name.includes('6tb') || name.includes('8tb');
  });

  const hdd = suitable.length > 0 ? suitable[0] : hdds[0];
  
  return {
    product: hdd,
    reason: `Surveillance-grade HDD for ${storageDays}-day recording retention`,
    quantity: 1,
    role: 'hdd',
  };
}

export function recommendPoESwitch(
  products: CatalogProduct[],
  cameraCount: number
): ProductRecommendation | undefined {
  const switches = products.filter(p => {
    if (!p.inStock || p.stock <= 0) return false;
    if (p.status !== 'active') return false;
    return ['sub23'].includes(p.subcategoryId || '') ||
      p.name.toLowerCase().includes('poe switch') ||
      p.name.toLowerCase().includes('poe');
  });

  if (switches.length === 0) return undefined;

  const suitable = switches.filter(p => {
    const name = p.name.toLowerCase();
    if (cameraCount <= 4) return name.includes('4 port') || name.includes('4port') || name.includes('4-port');
    if (cameraCount <= 8) return name.includes('8 port') || name.includes('8port') || name.includes('8-port');
    return true;
  });

  const sw = suitable.length > 0 ? suitable[0] : switches[0];
  
  return {
    product: sw,
    reason: `PoE switch to power ${cameraCount} IP cameras via network cable`,
    quantity: 1,
    role: 'poe-switch',
  };
}

export function buildSolution(
  products: CatalogProduct[],
  req: ParsedRequirement
): SolutionRecommendation {
  const cameraCount = req.cameraQuantity || 4;
  const storageDays = req.storagedays || 30;
  const isWifi = !!req.wifiCamera;

  const cameras = recommendCameras(products, req);
  const recorder = isWifi && cameraCount <= 4 
    ? undefined 
    : recommendRecorder(products, cameraCount, isWifi);
  const storage = recorder ? recommendStorage(products, cameraCount, storageDays) : undefined;
  const poeSwitch = !isWifi && recorder ? recommendPoESwitch(products, cameraCount) : undefined;

  // Installation cost estimate
  const installPerCamera = 1500;
  const installBase = 5000;
  const installationCost = installBase + (cameraCount * installPerCamera);
  const labourCost = cameraCount <= 4 ? 5000 : cameraCount <= 8 ? 8000 : 12000;

  // Calculate total
  let total = installationCost + labourCost;
  if (cameras.length > 0) {
    const cam = cameras[0];
    const price = cam.product.salePrice || cam.product.regularPrice;
    total += price * cameraCount;
  }
  if (recorder) {
    total += recorder.product.salePrice || recorder.product.regularPrice;
  }
  if (storage) {
    total += storage.product.salePrice || storage.product.regularPrice;
  }
  if (poeSwitch) {
    total += poeSwitch.product.salePrice || poeSwitch.product.regularPrice;
  }

  // Cable estimate: ~150m for average install @ Rs.35/m
  const cableEstimate = 5250;
  total += cableEstimate;

  const accessories: ProductRecommendation[] = [];
  if (poeSwitch) accessories.push(poeSwitch);

  const notes: string[] = [];
  if (req.fullColour) notes.push('Full colour night vision: Records in colour 24/7, even without external lighting');
  if (req.humanDetection) notes.push('Human detection enabled: AI alerts on human movement');
  if (req.remoteViewing !== false) notes.push('Remote viewing via mobile app included');
  notes.push('Professional installation by K-Tec certified technicians');
  notes.push('Prices are estimates — final quotation subject to site survey');

  const storageEstimate = `Estimated storage: ${(cameraCount * 8 * storageDays / 1000).toFixed(1)}TB for ${storageDays}-day retention`;

  return {
    title: `${cameraCount}-Camera ${req.fullColour ? 'Full Colour ' : ''}${isWifi ? 'Wi-Fi ' : 'IP '}CCTV Solution`,
    cameras,
    recorder,
    storage,
    network: poeSwitch,
    accessories,
    installationCost,
    labourCost,
    totalEstimate: total,
    notes,
    storageEstimate,
  };
}

export function recommendPackage(
  packages: CCTVPackage[],
  req: ParsedRequirement
): CCTVPackage | undefined {
  const cameraCount = req.cameraQuantity;
  if (!cameraCount) return undefined;

  // Find packages matching camera count
  const matching = packages.filter(pkg => {
    if (!pkg.isActive) return false;
    if (pkg.cameraCount && pkg.cameraCount === cameraCount) return true;
    if (pkg.cameraCount && Math.abs(pkg.cameraCount - cameraCount) <= 1) return true;
    return false;
  });

  if (matching.length === 0) return undefined;

  // Filter by budget if specified
  if (req.budget) {
    const withinBudget = matching.filter(pkg => {
      const price = pkg.totalPrice || 0;
      return price <= req.budget! * 1.1; // 10% tolerance
    });
    if (withinBudget.length > 0) return withinBudget[0];
  }

  return matching[0];
}
